'use client';

import { Skeleton } from '@common/components';
import { cn } from '@core/utils';
import { useParams } from 'next/navigation';
import { useSyncExternalStore } from 'react';

import { buildGiftHeroImageUrl } from '@/app/_components/GiftCard/giftImage';
import { ShareButton } from '@/app/donation/_components/ShareButton';
import { ThumbnailBackedImage } from '@/app/donation/gifts/[productNumber]/_components/GiftDetailHeader/_components/ThumbnailBackedImage';
import { formatPrice } from '@/utils/formatPrice';

import {
  getCampaignPreviewSnapshot,
  matchCampaignPreview,
  subscribeCampaignPreview,
} from '../../_utils/campaignPreview';

const DESKTOP_THUMBNAIL_SLOT_COUNT = 6;
const INFO_ROW_COUNT = 3;

//* loading.tsx 안에 client 컴포넌트를 두면 Next의 scroll-to-top이 죽어(실측) 로딩 경계 밖 layout에서 그린다
//* 래퍼·간격·타이포는 page.tsx·CampaignSummary 마크업을 폭별로 그대로 따른다 — 모르는 값만 같은 줄높이의 스켈레톤으로 채워야 본문 도착 시 시프트가 0이다
export const CampaignEntryPreview = () => {
  const params = useParams<{ campaignId: string }>();
  const tapped = useSyncExternalStore(
    subscribeCampaignPreview,
    getCampaignPreviewSnapshot,
    () => null,
  );
  const preview = matchCampaignPreview(tapped, params?.campaignId);

  if (!preview) {
    return null;
  }

  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 z-[2] overflow-hidden bg-Gray-white"
    >
      <div
        className={cn(
          'flex flex-col px-40 pt-20 content-container',
          'mobile-tablet:px-0 mobile-tablet:pt-0',
        )}
      >
        <div
          className={cn(
            'grid grid-cols-2 gap-28',
            'mobile-tablet:grid-cols-1 mobile-tablet:gap-0',
          )}
        >
          <div className="hidden mobile:block tablet:block">
            <div className="relative aspect-square w-full bg-Gray-black/10">
              <ThumbnailBackedImage
                key={preview.campaignId}
                src={buildGiftHeroImageUrl(preview.mainImageUrl)}
                alt={preview.productName}
                thumbnailSrc={preview.thumbnailSrc}
                loading="eager"
              />
            </div>
          </div>
          <div className="mobile:hidden tablet:hidden">
            <div className="relative mb-20 aspect-square">
              <div className="relative size-full overflow-hidden rounded-[48px] bg-Gray-black/10">
                <ThumbnailBackedImage
                  key={preview.campaignId}
                  src={buildGiftHeroImageUrl(preview.mainImageUrl)}
                  alt={preview.productName}
                  thumbnailSrc={preview.thumbnailSrc}
                  loading="eager"
                />
              </div>
            </div>
            {/* 실제 장수는 본문이 와야 알 수 있어 한 줄(6칸) 높이만 잡는다 */}
            <div className="grid grid-cols-6 gap-10">
              {Array.from({ length: DESKTOP_THUMBNAIL_SLOT_COUNT }).map(
                (_, index) => (
                  <Skeleton
                    key={index}
                    className="aspect-square w-full rounded-large"
                  />
                ),
              )}
            </div>
          </div>
          <div className="flex min-w-0 flex-col gap-20 break-keep px-20 mobile-tablet:relative mobile-tablet:z-[1] mobile-tablet:-mt-20 mobile-tablet:rounded-t-large mobile-tablet:bg-Gray-white mobile-tablet:px-0 mobile-tablet:pt-24">
            <div className="flex flex-col gap-28 mobile-tablet:gap-16 mobile-tablet:px-24">
              <div className="flex flex-col gap-8">
                <div className="flex items-center justify-between gap-8">
                  <p className="font-600 text-Gray-600 typo-body3 mobile-tablet:font-400 mobile-tablet:typo-body4">
                    {preview.institutionName}
                  </p>
                  <div className="invisible flex">
                    <ShareButton />
                  </div>
                </div>
                <h2 className="font-700 text-Gray-900 typo-h3 mobile-tablet:font-600 mobile-tablet:text-Gray-black mobile-tablet:typo-body1">
                  {preview.productName}
                </h2>
              </div>
              <div className="hidden flex-col mobile-tablet:flex">
                <s className="font-700 text-Gray-550 typo-body1 mobile-tablet:font-600 mobile-tablet:typo-body3">
                  {formatPrice(preview.marketPrice)}
                </s>
                <p className="flex items-center gap-8 font-600 text-O-400 typo-body3 mobile-tablet:items-end mobile-tablet:gap-4 mobile-tablet:typo-body4">
                  <strong className="font-700 typo-h3 mobile-tablet:typo-h5">
                    0원
                  </strong>
                  <span className="mobile-tablet:pb-2">체험단 선정 시</span>
                </p>
              </div>
            </div>

            <div className="hidden mobile-tablet:block mobile-tablet:px-24">
              <div className="flex flex-col gap-12">
                <Skeleton className="h-24 w-96 rounded-xsmall" />
                <div className="flex flex-col gap-12">
                  {Array.from({ length: INFO_ROW_COUNT }).map((_, index) => (
                    <Skeleton
                      key={index}
                      typo="body4"
                      className="w-full rounded-xxsmall"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
